import { Link } from "react-router-dom";
import {
  CheckIcon as Check,
  PencilSimpleIcon as Pencil,
  TrashIcon as Trash,
  FolderIcon as Folder,
  SparkleIcon as Sparkle,
  ShareNetworkIcon as ShareNetwork,
  FilePdfIcon as FilePdf,
  DatabaseIcon as Database,
} from "@phosphor-icons/react";

export default function WorkspaceHeader({
  workspace,
  workspaceId,
  editMode,
  setEditMode,
  form,
  setForm,
  copied,
  exportingPdf,
  onSave,
  onDelete,
  onShare,
  onExportCombinedPdf,
  onOpenPromote,
}) {
  return (
    <div className="border-b border-[var(--ml-border)] pb-6 mb-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-8 flex items-start gap-4">
        <div className="w-12 h-12 bg-[var(--ml-primary)] flex items-center justify-center shrink-0">
          <Folder size={24} weight="bold" color="#fff" />
        </div>
        {editMode ? (
          <EditForm
            form={form}
            setForm={setForm}
            onSave={onSave}
            onCancel={() => setEditMode(false)}
          />
        ) : (
          <ReadOnlyHeader workspace={workspace} />
        )}
      </div>
      <div className="lg:col-span-4 flex flex-wrap gap-2 lg:justify-end">
        <button
          onClick={onOpenPromote}
          className="ml-btn-primary"
          data-testid="promote-workspace-btn"
        >
          <Sparkle size={14} weight="bold" /> Promote to client
        </button>
        <button
          onClick={onExportCombinedPdf}
          className="ml-btn-secondary"
          disabled={exportingPdf}
          data-testid="combined-pdf-btn"
        >
          <FilePdf size={14} weight="bold" />{" "}
          {exportingPdf ? "Building PDF…" : "Combined PDF"}
        </button>
        <button
          onClick={onShare}
          className="ml-btn-secondary"
          data-testid="share-workspace-btn"
        >
          <ShareNetwork size={14} weight="bold" />{" "}
          {copied ? "Link copied!" : "Share link"}
        </button>
        <Link
          to={`/workspaces/${workspaceId}/metadata`}
          className="ml-btn-secondary"
          data-testid="metadata-repo-link"
        >
          <Database size={14} weight="bold" /> Metadata repo
        </Link>
        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="ml-btn-ghost"
            data-testid="edit-workspace-btn"
          >
            <Pencil size={14} /> Edit
          </button>
        )}
        <button
          onClick={onDelete}
          className="ml-btn-ghost text-[var(--ml-error)]"
          data-testid="delete-workspace-detail-btn"
        >
          <Trash size={14} />
        </button>
      </div>
    </div>
  );
}

function ReadOnlyHeader({ workspace }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
        Workspace
      </div>
      <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">
        {workspace.name}
      </h1>
      {workspace.client_name && (
        <div className="text-[var(--ml-text-secondary)] mt-1">
          {workspace.client_name}
        </div>
      )}
      {workspace.description && (
        <p className="text-sm text-[var(--ml-text-primary)] mt-3 leading-relaxed max-w-2xl">
          {workspace.description}
        </p>
      )}
    </div>
  );
}

function EditForm({ form, setForm, onSave, onCancel }) {
  return (
    <div className="flex-1 space-y-3">
      <input
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        className="ml-input text-xl font-semibold"
        data-testid="edit-workspace-name"
      />
      <input
        value={form.client_name}
        onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
        className="ml-input text-sm"
        placeholder="Client name"
        data-testid="edit-workspace-client"
      />
      <textarea
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        rows={2}
        className="ml-textarea text-sm"
        placeholder="Description"
        data-testid="edit-workspace-description"
      />
      <div className="flex gap-2">
        <button onClick={onSave} className="ml-btn-primary" data-testid="save-workspace-btn">
          <Check size={16} weight="bold" /> Save
        </button>
        <button onClick={onCancel} className="ml-btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  );
}
